// NewTripForm.jsx
import React, { useEffect, useState, useRef } from "react";
import { createTrip, fetchCategories, updateTrip } from "../../services/tripServices";
import { Editor } from '@tinymce/tinymce-react';
import { TINY_MCE_API_KEY } from "../../config";

const moveItem = (arr, from, to) => {
    const copy = [...arr];
    const item = copy.splice(from, 1)[0];
    copy.splice(to, 0, item);
    return copy;
};

export default function NewTripForm({ initialData }) {
    const editorRef = useRef(null);

    const [formData, setFormData] = useState({
        title: initialData?.title || "",
        slug: initialData?.slug || "",
        category: initialData?.category || "",
        price: initialData?.price || "",
        duration: initialData?.duration || "",
        location: initialData?.location || "",
        thumbnailImage: null,
        galleryImages: [],
        description: initialData?.description || "",
        overview: initialData?.overview || "",
    });
    // ITINERARY
    const [itineraryForm, setItineraryForm] = useState({ title: "", details: "", altitude: "", accommodations: "", meals: "" });
    const [itinerary, setItinerary] = useState(initialData ? JSON.parse(initialData.itinerary) : []);

    // FAQs
    const [faqForm, setFaqForm] = useState({ question: "", answer: "" });
    const [faqs, setFaqs] = useState(initialData ? JSON.parse(initialData.faqs) : []);
    // VARIATIONS
    const [variationForm, setVariationForm] = useState({ price: "", dateRange: "", seatsAvailable: "" });
    const [variations, setVariations] = useState(initialData ? JSON.parse(initialData.variations) : []);

    // INCLUSIONS / EXCLUSIONS
    const [inclusionText, setInclusionText] = useState("");
    const [inclusions, setInclusions] = useState(initialData ? JSON.parse(initialData.inclusions) : []);

    const [exclusionText, setExclusionText] = useState("");
    const [exclusions, setExclusions] = useState(initialData ? JSON.parse(initialData.exclusions) : []);

    const [categories, setCategories] = useState([]);

    useEffect(() => {
        fetchCategories().then(data => setCategories(data));
    }, []);

    const [submitting, setSubmitting] = useState(false);
    const onSubmit = (e) => {
        e.preventDefault();
        setSubmitting(true);
        // Handle form submission logic here
        const newTripData = new FormData();
        newTripData.append("title", formData.title);
        newTripData.append("category", formData.category);
        newTripData.append("price", formData.price);
        newTripData.append("duration", formData.duration);
        newTripData.append("location", formData.location);
        newTripData.append("description", formData.description);
        newTripData.append("overview", editorRef.current.getContent());
        newTripData.append("itinerary", JSON.stringify(itinerary));
        newTripData.append("faqs", JSON.stringify(faqs));
        newTripData.append("variations", JSON.stringify(variations));
        newTripData.append("inclusions", JSON.stringify(inclusions));
        newTripData.append("exclusions", JSON.stringify(exclusions));
        newTripData.append("slug", formData.slug);
        if (formData.thumbnailImage) {
            newTripData.append("thumbnailImage", formData.thumbnailImage);
        }
        if (formData.galleryImages && formData.galleryImages.length > 0) {
            Array.from(formData.galleryImages).forEach((file, index) => {
                newTripData.append("galleryImages", file);
            });
        }

        try {
            initialData ? updateTrip(initialData._id, newTripData).then((data) => {
                if (data) alert("Trip updated successfully!");
                setSubmitting(false);
            }) : createTrip(newTripData).then(data => {
                alert("Trip created successfully!");
                setSubmitting(false);
            });
        }
        catch (error) {
            alert("Error creating trip. Please try again.");
            setSubmitting(false);
        }
    };

    return (
        <section className="new-trip">
            <h1>{initialData ? "Edit Trip" : "Create New Trip"}</h1>
            <div className="form-grid">
                <input value={formData.title} placeholder="Title" onChange={(e) => setFormData({ ...formData, title: e.target.value })} required />
                <input value={formData.slug} placeholder="Slug" onChange={(e) => setFormData({ ...formData, slug: e.target.value })} required />

                <select value={formData.category} onChange={(e) => setFormData({ ...formData, category: e.target.value })} required >
                    <option value="" disabled>Select Category</option>
                    {categories && categories.map(cat => (<option key={cat.id} value={`${cat.name}-${cat.type}`}>{cat.name + ", " + cat.type}</option>))}
                </select>
                <input value={formData.price} placeholder="Price" onChange={(e) => setFormData({ ...formData, price: e.target.value })} required />
                <input value={formData.duration} placeholder="Duration" onChange={(e) => setFormData({ ...formData, duration: e.target.value })} />
                <input value={formData.location} placeholder="Location" onChange={(e) => setFormData({ ...formData, location: e.target.value })} />
                <input type="file" accept="image/*" placeholder="Thumbnail Image URL" onChange={(e) => setFormData({ ...formData, thumbnailImage: e.target.files[0] })} required />
                <input type="file" accept="image/*" multiple placeholder="Gallery Images (comma separated URLs)" onChange={(e) => setFormData({ ...formData, galleryImages: e.target.files })} />
            </div>


            <textarea value={formData.description} cols={100} placeholder="Short Description" required onChange={(e) => setFormData({ ...formData, description: e.target.value })} />
            <textarea value={formData.keywords} cols={100} placeholder="KeyWords" required onChange={(e) => setFormData({ ...formData, keywords: e.target.value })} />

            <div className="rich-text">
                <label>Overview</label>
                <Editor
                    apiKey={TINY_MCE_API_KEY}
                    onInit={(_evt, editor) => editorRef.current = editor}
                    initialValue={formData.overview}
                    init={{
                        height: 500,
                        menubar: false,
                        plugins: [
                            'advlist', 'autolink', 'lists', 'link', 'image', 'charmap', 'preview',
                            'anchor', 'searchreplace', 'visualblocks', 'code', 'fullscreen',
                            'insertdatetime', 'media', 'table', 'code', 'help', 'wordcount'
                        ],
                        toolbar: 'undo redo | blocks | ' +
                            'bold italic forecolor | alignleft aligncenter ' +
                            'alignright alignjustify | bullist numlist outdent indent | ' +
                            'removeformat | help',
                        content_style: 'body { font-family:Helvetica,Arial,sans-serif; font-size:14px }'
                    }}
                />
            </div>


            {/* ITINERARY */}
            <section className="child-section">
                <h2>Itinerary</h2>
                <div className="child-form">
                    <input placeholder="Title" value={itineraryForm.title} onChange={e => setItineraryForm({ ...itineraryForm, title: e.target.value })} />
                    <textarea placeholder="Details" value={itineraryForm.details} onChange={e => setItineraryForm({ ...itineraryForm, details: e.target.value })} />
                    <input placeholder="Altitude" value={itineraryForm.altitude} onChange={e => setItineraryForm({ ...itineraryForm, altitude: e.target.value })} />
                    <input placeholder="Accommodations" value={itineraryForm.accommodations} onChange={e => setItineraryForm({ ...itineraryForm, accommodations: e.target.value })} />
                    <input placeholder="Meals" value={itineraryForm.meals} onChange={e => setItineraryForm({ ...itineraryForm, meals: e.target.value })} />
                    <button onClick={() => {
                        setItinerary([...itinerary, itineraryForm]);
                        setItineraryForm({ title: "", details: "", altitude: "", accommodations: "", meals: "" });
                    }}>Add Day</button>
                </div>

                {itinerary.length > 0 && (
                    <table className="data-table">
                        <thead>
                            <tr><th>Day</th><th>Title</th><th>Altitude</th><th>Actions</th></tr>
                        </thead>
                        <tbody>
                            {itinerary.map((item, i) => (
                                <tr key={i}>
                                    <td>{i + 1}</td>
                                    <td>{item.title}</td>
                                    <td>{item.altitude}</td>
                                    <td className="actions">
                                        <button disabled={i === 0} onClick={() => setItinerary(moveItem(itinerary, i, i - 1))}>↑</button>
                                        <button disabled={i === itinerary.length - 1} onClick={() => setItinerary(moveItem(itinerary, i, i + 1))}>↓</button>
                                        <button onClick={() => setItinerary(itinerary.filter((_, idx) => idx !== i))}>✕</button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </section>

            {/* FAQs */}
            <section className="child-section">
                <h2>FAQs</h2>
                <div className="child-form">
                    <input placeholder="Question" value={faqForm.question} onChange={e => setFaqForm({ ...faqForm, question: e.target.value })} />
                    <textarea placeholder="Answer" value={faqForm.answer} onChange={e => setFaqForm({ ...faqForm, answer: e.target.value })} />
                    <button onClick={() => {
                        setFaqs([...faqs, faqForm]);
                        setFaqForm({ question: "", answer: "" });
                    }}>Add FAQ</button>
                </div>

                {faqs.length > 0 && (
                    <table className="data-table">
                        <thead><tr><th>Question</th><th>Answer</th><th>Actions</th></tr></thead>
                        <tbody>
                            {faqs.map((item, i) => (
                                <tr key={i}>
                                    <td>{item.question}</td>
                                    <td>{item.answer}</td>

                                    <td className="actions">
                                        <button disabled={i === 0} onClick={() => setFaqs(moveItem(faqs, i, i - 1))}>↑</button>
                                        <button disabled={i === faqs.length - 1} onClick={() => setFaqs(moveItem(faqs, i, i + 1))}>↓</button>
                                        <button onClick={() => setFaqs(faqs.filter((_, idx) => idx !== i))}>✕</button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </section>

            {/* VARIATIONS */}
            <section className="child-section">
                <h2>Available Dates</h2>
                <div className="child-form">
                    <input placeholder="Price" value={variationForm.price} onChange={e => setVariationForm({ ...variationForm, price: e.target.value })} />
                    <input placeholder="Date Range" value={variationForm.dateRange} onChange={e => setVariationForm({ ...variationForm, dateRange: e.target.value })} />
                    <input placeholder="Seats Available" value={variationForm.seatsAvailable} onChange={e => setVariationForm({ ...variationForm, seatsAvailable: e.target.value })} />
                    <button onClick={() => {
                        setVariations([...variations, variationForm]);
                        setVariationForm({ price: "", dateRange: "", seatsAvailable: 0 });
                    }}>Add Variation</button>
                </div>

                {variations.length > 0 && (
                    <table className="data-table">
                        <thead><tr><th>Price</th><th>Date Range</th><th>Actions</th></tr></thead>
                        <tbody>
                            {variations.map((item, i) => (
                                <tr key={i}>
                                    <td>{item.price}</td>
                                    <td>{item.dateRange}</td>
                                    <td className="actions">
                                        <button disabled={i === 0} onClick={() => setVariations(moveItem(variations, i, i - 1))}>↑</button>
                                        <button disabled={i === variations.length - 1} onClick={() => setVariations(moveItem(variations, i, i + 1))}>↓</button>
                                        <button onClick={() => setVariations(variations.filter((_, idx) => idx !== i))}>✕</button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </section>

            {/* INCLUSIONS */}
            <section className="child-section">
                <h2>Inclusions</h2>
                <div className="child-form single">
                    <input placeholder="Inclusion" value={inclusionText} onChange={e => setInclusionText(e.target.value)} />
                    <button onClick={() => { setInclusions([...inclusions, inclusionText]); setInclusionText(""); }}>Add</button>
                </div>

                {inclusions.length > 0 && (
                    <table className="data-table">
                        <thead><tr><th>Item</th><th>Actions</th></tr></thead>
                        <tbody>
                            {inclusions.map((item, i) => (
                                <tr key={i}>
                                    <td>{item}</td>
                                    <td className="actions">
                                        <button onClick={() => setInclusions(inclusions.filter((_, idx) => idx !== i))}>✕</button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </section>

            {/* EXCLUSIONS */}
            <section className="child-section">
                <h2>Exclusions</h2>
                <div className="child-form single">
                    <input placeholder="Exclusion" value={exclusionText} onChange={e => setExclusionText(e.target.value)} />
                    <button onClick={() => { setExclusions([...exclusions, exclusionText]); setExclusionText(""); }}>Add</button>
                </div>

                {exclusions.length > 0 && (
                    <table className="data-table">
                        <thead><tr><th>Item</th><th>Actions</th></tr></thead>
                        <tbody>
                            {exclusions.map((item, i) => (
                                <tr key={i}>
                                    <td>{item}</td>
                                    <td className="actions">
                                        <button onClick={() => setExclusions(exclusions.filter((_, idx) => idx !== i))}>✕</button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </section>
                
            <button disabled={submitting} className="submit" onClick={onSubmit}>{submitting ? initialData ? "Updating..." : "Creating..." : initialData ? "Update Trip" : "Create Trip"}</button>
        </section>
    );
}
